/**
 * Admin → Audit log.
 *
 * Every assessment reset an admin performs is written to the runtime audit
 * trail. This page reads it back: who reset, whose test, when, why, and what
 * the reset actually touched. Search covers learner email/name, the admin who
 * acted, and the reason text.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, RefreshCw, Search, ShieldCheck } from "lucide-react";

type ResetEvent = {
  id: string;
  occurred_at: string;
  learner_id: string | null;
  learner_email: string | null;
  learner_name: string | null;
  actor_id: string | null;
  actor_email: string | null;
  reason: string | null;
  assessments_archived: number | null;
  sessions_closed: number | null;
  state_keys_cleared: number | null;
};

const PAGE_SIZE = 50;

function fmt(ts: string | null) {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const AdminAuditLog = () => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [rows, setRows] = useState<ResetEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => setPage(0), [debounced]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("admin_list_reset_events", {
      _search: debounced || null,
      _limit: PAGE_SIZE,
      _offset: page * PAGE_SIZE,
    });
    setLoading(false);
    if (error) {
      toast({ title: "Couldn't load the audit log", description: error.message, variant: "destructive" });
      setRows([]);
      return;
    }
    setRows((data ?? []) as ResetEvent[]);
  }, [debounced, page, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo(
    () => ({
      events: rows.length,
      archived: rows.reduce((n, r) => n + (r.assessments_archived ?? 0), 0),
      sessions: rows.reduce((n, r) => n + (r.sessions_closed ?? 0), 0),
    }),
    [rows],
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon" aria-label="Back to Admin Hub">
            <Link to="/admin">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-primary" /> Audit log
            </h1>
            <p className="text-sm text-muted-foreground">
              Assessment resets — who did it, whose test, when, and why.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Card className="rounded-xl">
            <CardContent className="py-3">
              <div className="text-xs text-muted-foreground">Events (page)</div>
              <div className="text-xl font-semibold">{totals.events}</div>
            </CardContent>
          </Card>
          <Card className="rounded-xl">
            <CardContent className="py-3">
              <div className="text-xs text-muted-foreground">Takes archived</div>
              <div className="text-xl font-semibold">{totals.archived}</div>
            </CardContent>
          </Card>
          <Card className="rounded-xl">
            <CardContent className="py-3">
              <div className="text-xs text-muted-foreground">Sessions closed</div>
              <div className="text-xl font-semibold">{totals.sessions}</div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9 min-h-[44px]"
              placeholder="Search learner, admin or reason…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search reset events"
            />
          </div>
          <Button variant="outline" onClick={() => void load()} disabled={loading} aria-label="Refresh audit log">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <Card className="rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Reset events</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground py-6">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6">
                No reset events{debounced ? " match that search" : " yet"}.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Learner</TableHead>
                    <TableHead>Admin</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Effect</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap text-sm">{fmt(r.occurred_at)}</TableCell>
                      <TableCell className="text-sm">
                        <div className="font-medium">{r.learner_email ?? r.learner_id ?? "—"}</div>
                        {r.learner_name && (
                          <div className="text-xs text-muted-foreground">{r.learner_name}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{r.actor_email ?? r.actor_id ?? "—"}</TableCell>
                      <TableCell className="text-sm max-w-[280px]">
                        {r.reason ?? <span className="text-muted-foreground">No reason recorded</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap gap-1 justify-end">
                          <Badge variant="outline">{r.assessments_archived ?? 0} archived</Badge>
                          <Badge variant="outline">{r.sessions_closed ?? 0} sessions</Badge>
                          <Badge variant="outline">{r.state_keys_cleared ?? 0} cleared</Badge>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
          >
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">Page {page + 1}</span>
          <Button
            variant="outline"
            onClick={() => setPage((p) => p + 1)}
            disabled={rows.length < PAGE_SIZE || loading}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminAuditLog;
